/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Download, Eye,
  CheckCircle, XCircle, Clock, AlertTriangle,
  DollarSign, Calendar, FileCheck, RefreshCw,
  ChevronLeft, ChevronRight, FileX
} from 'lucide-react';
import { useSearchInvoices, useInvoiceStats, useDownloadInvoicePdfVariant } from '@/hooks/api/useInvoices';
import { useTheme } from '@/hooks';
import { Invoice, InvoiceStatus } from '@/types/superadmin.types';
import { SortableHeader, useSort } from '@/components/ui/SortableHeader';
import { SearchInput } from '@/components/ui';

// Status Badge
const StatusBadge: React.FC<{ status: InvoiceStatus; t: (key: string) => string }> = ({ status, t }) => {
  const config: Record<InvoiceStatus, { bg: string; text: string; icon: typeof CheckCircle; labelKey: string }> = {
    DRAFT: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: FileText, labelKey: 'invoices.status.draft' },
    ISSUED: { bg: 'bg-blue-500/15', text: 'text-blue-500', icon: FileCheck, labelKey: 'invoices.status.issued' },
    PAID: { bg: 'bg-emerald-500/15', text: 'text-emerald-500', icon: CheckCircle, labelKey: 'invoices.status.paid' },
    CANCELLED: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: XCircle, labelKey: 'invoices.status.cancelled' },
    OVERDUE: { bg: 'bg-red-500/15', text: 'text-red-500', icon: AlertTriangle, labelKey: 'invoices.status.overdue' },
    VOID: { bg: 'bg-purple-500/15', text: 'text-purple-500', icon: FileX, labelKey: 'invoices.status.void' },
  };
  const { bg, text, icon: Icon, labelKey } = config[status] || config.DRAFT;

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {t(labelKey)}
    </span>
  );
};

// Skeleton Row Component
const SkeletonRow: React.FC = () => (
  <div className="invoices-table-row grid grid-cols-[1fr_1.5fr_100px_100px_120px_100px_80px] gap-4 px-4 py-3 items-center border-b border-border-subtle animate-pulse">
    <div className="h-4 w-28 bg-muted/20 rounded"></div>
    <div className="h-4 w-32 bg-muted/20 rounded"></div>
    <div className="h-5 w-16 bg-muted/20 rounded"></div>
    <div className="h-5 w-16 bg-muted/20 rounded-full"></div>
    <div className="h-4 w-20 bg-muted/20 rounded"></div>
    <div className="h-4 w-20 bg-muted/20 rounded"></div>
    <div className="w-7 h-7 bg-muted/20 rounded"></div>
  </div>
);

// Stat Card Skeleton
const StatCardSkeleton: React.FC = () => (
  <div className="bg-surface border border-border-subtle rounded-xl p-4 animate-pulse">
    <div className="flex items-center justify-between mb-2">
      <div className="w-10 h-10 rounded-lg bg-muted/20"></div>
    </div>
    <div className="h-6 w-20 bg-muted/20 rounded mb-1"></div>
    <div className="h-3 w-28 bg-muted/20 rounded"></div>
  </div>
);

// Stat Card
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string;
  label: string;
  color: 'green' | 'blue' | 'red' | 'amber' | 'gray';
}> = ({ icon, value, label, color }) => {
  const colors = {
    green: 'bg-emerald-500/15 text-emerald-500',
    blue: 'bg-blue-500/15 text-blue-500',
    red: 'bg-red-500/15 text-red-500',
    amber: 'bg-amber-500/15 text-amber-500',
    gray: 'bg-gray-500/15 text-gray-400',
  };

  return (
    <div className="bg-surface border border-border-subtle rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          {icon}
        </div>
      </div>
      <div className="font-mono text-3xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted">{label}</div>
    </div>
  );
};

// Main Invoices Page
export default function InvoicesPage() {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'ALL'>('ALL');
  const [currentPage, setCurrentPage] = useState(0);

  // Sort state
  const { sortConfig, handleSort, getSortParam } = useSort('issueDate', 'desc');

  // Use the invoices hook with filters
  const { data, isLoading, isFetching, refetch } = useSearchInvoices({
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    search: searchQuery || undefined,
    page: currentPage,
    size: 20,
    sort: getSortParam(),
  });

  // Fetch stats
  const { data: stats, isLoading: statsLoading } = useInvoiceStats();
  const downloadPdfVariant = useDownloadInvoicePdfVariant();

  const invoices = data?.content || [];
  const totalElements = data?.page?.totalElements || 0;
  const totalPages = data?.page?.totalPages || 1;
  const pageSize = 20;

  // Handle view invoice
  const handleViewInvoice = (invoice: Invoice) => {
    navigate(`/superadmin/invoices/${invoice.id}`);
  };

  // Handle download PDF with theme-aware variant (with authentication)
  const handleDownloadPdf = async (invoice: Invoice) => {
    if (invoice.hasPdf && !downloadPdfVariant.isPending) {
      const language = t('common.language') === 'fr' ? 'fr' : 'en';
      const variant = isDark ? 'blackops' : 'tactical';
      const filename = invoice.invoiceNumber ? `${invoice.invoiceNumber}.pdf` : undefined;
      await downloadPdfVariant.mutateAsync({
        id: invoice.id,
        variant,
        language,
        filename,
      });
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    if (currency === 'USD') return `$${amount.toLocaleString()}`;
    if (currency === 'GNF') return `${amount.toLocaleString()} FG`;
    if (currency === 'XOF' || currency === 'XAF') return `${amount.toLocaleString()} CFA`;
    return `${amount.toLocaleString()} ${currency}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="w-6 h-6" /> {t('invoices.title')}
          </h1>
          <p className="text-sm text-muted">{t('invoices.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-lg border border-border hover:bg-hover disabled:opacity-50"
            title={t('common.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-border-subtle rounded-lg text-sm font-medium text-foreground hover:bg-hover">
            <Download className="w-4 h-4" /> {t('common.export')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {statsLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon={<FileText className="w-5 h-5" />}
              value={(stats?.totalInvoices ?? 0).toString()}
              label={t('invoices.stats.total')}
              color="blue"
            />
            <StatCard
              icon={<CheckCircle className="w-5 h-5" />}
              value={(stats?.paidCount ?? 0).toString()}
              label={t('invoices.stats.paid')}
              color="green"
            />
            <StatCard
              icon={<Clock className="w-5 h-5" />}
              value={(stats?.issuedCount ?? 0).toString()}
              label={t('invoices.stats.issued')}
              color="amber"
            />
            <StatCard
              icon={<AlertTriangle className="w-5 h-5" />}
              value={(stats?.overdueCount ?? 0).toString()}
              label={t('invoices.stats.overdue')}
              color="red"
            />
            <StatCard
              icon={<DollarSign className="w-5 h-5" />}
              value={formatCurrency(stats?.outstandingAmountUsd ?? 0)}
              label={t('invoices.stats.outstanding')}
              color="amber"
            />
          </>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchQuery}
          onChange={(value) => {
            setSearchQuery(value);
            setCurrentPage(0);
          }}
          placeholder={t('invoices.searchPlaceholder')}
          className="flex-1 max-w-xs"
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as InvoiceStatus | 'ALL');
            setCurrentPage(0);
          }}
          className="px-3 py-2 bg-background border border-border-subtle rounded-lg text-sm text-foreground focus:outline-none"
        >
          <option value="ALL">{t('invoices.filters.allStatus')}</option>
          <option value="DRAFT">{t('invoices.status.draft')}</option>
          <option value="ISSUED">{t('invoices.status.issued')}</option>
          <option value="PAID">{t('invoices.status.paid')}</option>
          <option value="OVERDUE">{t('invoices.status.overdue')}</option>
          <option value="CANCELLED">{t('invoices.status.cancelled')}</option>
          <option value="VOID">{t('invoices.status.void')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
        <div className="invoices-table-header grid grid-cols-[1fr_1.5fr_100px_100px_120px_100px_80px] gap-4 px-4 py-3 bg-background border-b border-border-subtle">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">
            <SortableHeader
              columnKey="invoiceNumber"
              label={t('invoices.table.invoiceNo')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">
            <SortableHeader
              columnKey="customerName"
              label={t('invoices.table.customer')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-amount">
            <SortableHeader
              columnKey="totalUsd"
              label={t('invoices.table.amount')}
              sortConfig={sortConfig}
              onSort={handleSort}
              align="right"
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">
            <SortableHeader
              columnKey="status"
              label={t('invoices.table.status')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-date">
            <SortableHeader
              columnKey="issueDate"
              label={t('invoices.table.issueDate')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-due">
            <SortableHeader
              columnKey="dueDate"
              label={t('invoices.table.dueDate')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t('invoices.table.actions')}</div>
        </div>

        {isLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted">
            <FileText className="w-12 h-12 mb-3 opacity-50" />
            <p>{t('invoices.noInvoices')}</p>
          </div>
        ) : (
          invoices.map((invoice) => (
            <div
              key={invoice.id}
              className="invoices-table-row grid grid-cols-[1fr_1.5fr_100px_100px_120px_100px_80px] gap-4 px-4 py-3 items-center border-b border-border-subtle last:border-b-0 hover:bg-hover cursor-pointer"
              onClick={() => handleViewInvoice(invoice)}
            >
              <div className="font-mono text-sm font-medium text-foreground">
                {invoice.invoiceNumber || '-'}
              </div>
              <div className="text-sm text-foreground truncate">
                {invoice.customerName || '-'}
              </div>
              <div className="font-mono text-sm font-semibold text-foreground col-amount">
                {formatCurrency(invoice.totalUsd, invoice.billingCurrency)}
              </div>
              <div><StatusBadge status={invoice.status} t={t} /></div>
              <div className="text-sm text-muted col-date">{formatDate(invoice.issueDate)}</div>
              <div className={`text-sm col-due ${invoice.isOverdue ? 'text-red-500 font-medium' : 'text-muted'}`}>
                {formatDate(invoice.dueDate)}
              </div>
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => handleViewInvoice(invoice)}
                  className="p-1.5 rounded hover:bg-hover transition-colors text-muted hover:text-foreground"
                  title={t('invoices.view')}
                >
                  <Eye className="w-4 h-4" />
                </button>
                {invoice.hasPdf && (
                  <button
                    onClick={() => handleDownloadPdf(invoice)}
                    disabled={downloadPdfVariant.isPending}
                    className="p-1.5 rounded hover:bg-hover transition-colors text-muted hover:text-foreground disabled:opacity-50"
                    title={t('invoices.downloadPdf')}
                  >
                    {downloadPdfVariant.isPending ? (
                      <div className="w-4 h-4 border-2 border-muted/30 border-t-muted rounded-full animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {!isLoading && totalElements > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted">
            {t('invoices.pagination.showing', {
              from: currentPage * pageSize + 1,
              to: Math.min((currentPage + 1) * pageSize, totalElements),
              total: totalElements
            })}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted">
              {t('invoices.pagination.page', { current: currentPage + 1, total: Math.max(1, totalPages) })}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .invoices-table-header,
          .invoices-table-row {
            grid-template-columns: 1fr 1fr 80px 60px !important;
          }
          .col-date,
          .col-due {
            display: none !important;
          }
        }
        @media (max-width: 480px) {
          .invoices-table-header,
          .invoices-table-row {
            grid-template-columns: 1fr 60px 60px !important;
          }
          .col-amount {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
